/**
 * Public captain profile page mounted at `/captain/:id`.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type SyntheticEvent,
} from 'react';
import { useParams } from 'react-router-dom';

import { ApiClient, ApiError, type PublicCaptain } from '../api/ApiClient';
import { SUPPORT_EMAIL, SUPPORT_PHONE } from '../config/env';
import styles from './PublicCaptainPage.module.css';

const PLACEHOLDER_IMAGE_DATA_URI =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'>" +
      "<rect width='200' height='200' fill='#0f2044'/>" +
      "<circle cx='100' cy='80' r='40' fill='#1a3a6b'/>" +
      "<path d='M30 190c0-38 31-70 70-70s70 32 70 70' fill='#1a3a6b'/>" +
      '</svg>',
  );

const FETCH_TIMEOUT_MS = 10_000;

type Status = 'loading' | 'success' | 'not-found' | 'error';

function isNotFoundStatus(status: number): boolean {
  return status === 400 || status === 404;
}

/** Map department to color only (no emoji) */
function getDeptStyle(dept?: string): { color: string; bg: string } {
  if (!dept) return { color: '#94a3b8', bg: 'rgba(148,163,184,0.12)' };
  const d = dept.trim();
  if (d.includes('مدير') || d.includes('إدار') || d.includes('ادار'))
    return { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' };
  if (d.includes('قائد') || d.includes('كابتن') || d.includes('captain'))
    return { color: '#00c896', bg: 'rgba(0,200,150,0.12)' };
  if (d.includes('دعم') || d.includes('خدمة'))
    return { color: '#818cf8', bg: 'rgba(129,140,248,0.12)' };
  if (d.includes('مالي') || d.includes('محاسب'))
    return { color: '#34d399', bg: 'rgba(52,211,153,0.12)' };
  if (d.includes('تقني') || d.includes('تكنولوج'))
    return { color: '#60a5fa', bg: 'rgba(96,165,250,0.12)' };
  return { color: '#94a3b8', bg: 'rgba(148,163,184,0.12)' };
}

/** Returns true if this person is a captain (not management) */
function isCaptainRole(captain: PublicCaptain): boolean {
  const haystack = ((captain.jobTitle || '') + ' ' + (captain.department || '')).toLowerCase();
  const mgmtKeywords = ['مدير', 'ادار', 'إدار', 'تنفيذي', 'رئيس', 'مشرف', 'موظف', 'محاسب', 'تقني', 'دعم', 'خدمة'];
  return !mgmtKeywords.some(k => haystack.includes(k));
}

export default function PublicCaptainPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [status, setStatus] = useState<Status>('loading');
  const [captain, setCaptain] = useState<PublicCaptain | null>(null);
  const [imageFailed, setImageFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const requestIdRef = useRef(0);

  useEffect(() => {
    if (typeof id !== 'string' || id.length === 0) {
      setStatus('not-found');
      setCaptain(null);
      return;
    }

    const myRequestId = ++requestIdRef.current;
    setStatus('loading');
    setCaptain(null);
    setImageFailed(false);

    let settled = false;
    const timeoutHandle = window.setTimeout(() => {
      if (settled) return;
      if (requestIdRef.current !== myRequestId) return;
      settled = true;
      setStatus('error');
    }, FETCH_TIMEOUT_MS);

    ApiClient.getCaptain(id)
      .then((result) => {
        if (settled) return;
        if (requestIdRef.current !== myRequestId) return;
        settled = true;
        window.clearTimeout(timeoutHandle);
        setCaptain(result);
        setStatus('success');
      })
      .catch((err: unknown) => {
        if (settled) return;
        if (requestIdRef.current !== myRequestId) return;
        settled = true;
        window.clearTimeout(timeoutHandle);
        if (err instanceof ApiError && isNotFoundStatus(err.status)) {
          setStatus('not-found');
        } else {
          setStatus('error');
        }
      });

    return () => {
      window.clearTimeout(timeoutHandle);
      if (requestIdRef.current === myRequestId) {
        requestIdRef.current++;
      }
    };
  }, [id, attempt]);

  const handleRetry = useCallback(() => {
    setAttempt((n) => n + 1);
  }, []);

  const handleImageError = useCallback(
    (_e: SyntheticEvent<HTMLImageElement>) => {
      setImageFailed(true);
    },
    [],
  );

  return (
    <div className={styles.page} data-testid="public-captain-page">
      {/* Floating particles background */}
      <div className={styles.particles} aria-hidden="true">
        {[...Array(6)].map((_, i) => (
          <span key={i} className={styles.particle} style={{ '--i': i } as React.CSSProperties} />
        ))}
      </div>

      {/* Logo */}
      <header className={styles.logoHeader}>
        <img src="/logo.png" alt="وصل لي" className={styles.logoImg} />
      </header>

      {status === 'loading' && (
        <div className={styles.center}>
          <div className={styles.loadingBox} role="status" aria-live="polite">
            <div className={styles.spinner} aria-hidden="true" />
            <p className={styles.loadingText}>جاري التحميل...</p>
          </div>
        </div>
      )}

      {status === 'not-found' && (
        <div className={styles.center}>
          <div className={styles.notFoundBox}>
            <span className={styles.notFoundIcon} aria-hidden="true">🪪</span>
            <p>الكابتن غير موجود</p>
          </div>
        </div>
      )}

      {status === 'error' && (
        <div className={styles.center}>
          <div className={styles.errorBox} role="alert">
            <p>تعذّر تحميل بيانات الكابتن، يرجى المحاولة لاحقاً</p>
            <button type="button" className={styles.retryButton} onClick={handleRetry}>
              إعادة المحاولة
            </button>
          </div>
        </div>
      )}

      {status === 'success' && captain !== null && (() => {
        const deptStyle = getDeptStyle(captain.department);
        const whatsappNumber = captain.phoneNumber.replace(/[^0-9]/g, '');
        return (
          <div className={styles.center}>
            <article className={styles.card}>
              {/* Glow accent top */}
              <div className={styles.cardGlow} aria-hidden="true" />

              {/* Photo */}
              <div className={styles.photoWrapper}>
                <div className={styles.photoInner}>
                  <img
                    className={styles.photo}
                    src={imageFailed || !captain.imageUrl ? PLACEHOLDER_IMAGE_DATA_URI : captain.imageUrl}
                    alt={captain.name}
                    onError={handleImageError}
                  />
                </div>
              </div>

              {/* Name */}
              <h1 className={styles.name}>{captain.name}</h1>

              {/* Divider */}
              <div className={styles.divider} aria-hidden="true" />

              {/* Job Titles — show all */}
              {(() => {
                const titles =
                  Array.isArray(captain.jobTitles) && captain.jobTitles.length > 0
                    ? captain.jobTitles
                    : captain.jobTitle
                    ? [captain.jobTitle]
                    : [];
                return (
                  <div className={styles.jobTitlesWrapper}>
                    {titles.map((t, i) => (
                      <h2 key={i} className={`${styles.jobTitle} ${i > 0 ? styles.jobTitleSecondary : ''}`}>
                        {t}
                      </h2>
                    ))}
                  </div>
                );
              })()}

              {/* Department Badge — no emoji */}
              {captain.department && (
                <div
                  className={styles.deptBadge}
                  style={{
                    '--dept-color': deptStyle.color,
                    '--dept-bg': deptStyle.bg,
                  } as React.CSSProperties}
                >
                  {captain.department}
                </div>
              )}

              {/* Action buttons */}
              <div className={styles.actionsRow}>
                {/* Call button — always shown */}
                <a
                  className={styles.phoneLink}
                  href={`tel:${captain.phoneNumber}`}
                  aria-label={`اتصل بـ ${captain.name} على ${captain.phoneNumber}`}
                >
                  <span className={styles.btnIcon} aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.69a19.79 19.79 0 01-3.07-8.7A2 2 0 012 .82h3a2 2 0 012 1.72c.13 1 .37 1.97.72 2.9a2 2 0 01-.45 2.11l-1.27 1.27a16 16 0 006.29 6.29l1.27-1.27a2 2 0 012.11-.45c.93.35 1.9.59 2.9.72A2 2 0 0122 16.92z"/>
                    </svg>
                  </span>
                  <span className={styles.btnLabel}>{captain.phoneNumber}</span>
                </a>

                {/* WhatsApp — only for management roles */}
                {!isCaptainRole(captain) && (
                  <a
                    className={styles.whatsappLink}
                    href={`https://wa.me/${whatsappNumber}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`تواصل عبر واتساب مع ${captain.name}`}
                  >
                    <span className={styles.btnIcon} aria-hidden="true">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                      </svg>
                    </span>
                    <span className={styles.btnLabel}>واتساب</span>
                  </a>
                )}
              </div>

              {/* Card footer info strip */}
              <div className={styles.cardFooterStrip}>
                <span className={styles.stripItem}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                  معتمد من وصل لي
                </span>
              </div>
            </article>
          </div>
        );
      })()}

      <footer className={styles.footer}>
        <p className={styles.footerText}>في حال العثور على هذه البطاقة، يرجى التواصل معنا</p>
        <div className={styles.footerLinks}>
          {SUPPORT_PHONE && (
            <a
              className={styles.footerLink}
              href={`https://wa.me/${SUPPORT_PHONE.replace(/[^0-9]/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="تواصل عبر واتساب"
            >
              <span className={styles.footerLinkIcon}>💬</span>
              واتساب
            </a>
          )}
          {SUPPORT_EMAIL && (
            <a
              className={styles.footerLink}
              href={`mailto:${SUPPORT_EMAIL}`}
              aria-label={`راسلنا على ${SUPPORT_EMAIL}`}
            >
              <span className={styles.footerLinkIcon}>✉️</span>
              {SUPPORT_EMAIL}
            </a>
          )}
        </div>
      </footer>
    </div>
  );
}
